import moment from 'moment-timezone'
import {
  PENDING_SHIPMENT_STATUSES,
  TENDERED_SHIPMENT_STATUS,
  ON_HOLD_STATUS,
  SUBMITTED_STATUSES,
} from 'apps/booking/constants/shipments'
import { getApptUpdatedConfirmedInfo } from 'apps/booking/utils/stopUpdatedConfirmedUtils'
import { formatCurrency, formatPerMile } from 'utils/formatCurrency'
import { formatPhone } from 'utils/formatPhone'
import ManualEta from 'utils/manualEtaStatus'
import { displayDropTrailerStops } from 'utils/shipmentHelpers'

export interface IStop {
  appointment: {
    startAt: string
    endAt: string
    appointmentType: string
    timezone: string
  }
  facility: {
    detail: {
      schedulingOption: string
    }
  }
  manualEta: string
  project44Eta?: string
}

export interface ISidePanelShipment {
  equipment: {
    attributesSummary: string
  }
  status: {
    key: string
    currentStopIndex: number
  }
  info: {
    totalMiles: number
    driverPhone: string
    shipperPodId: string
  }
  stops: IStop[]
  coveredBid?: {
    amount: string
  }
  carrierPayment: {
    carrierRate: string
    tcmCarrierPrices?: {
      cmTarget: {
        amount: string
        shipmentRateId: number
      }
      listPrice?: string
    }
    strategicBuyRate: number
  }
  loadBoardPost: {
    rate: string
    includeListRate: boolean
  }
  customerCharges: {
    targetMarginRate: string
    total: string
  }
}

export interface IManualEta {
  lateDuration: string
  isLate: string | boolean
  dateTime: string
  date?: string
  time?: string
}

export const formatShipmentSidePanel = (data: ISidePanelShipment) => {
  const {
    carrierPayment,
    coveredBid,
    customerCharges,
    equipment,
    info,
    loadBoardPost,
    status,
    stops,
  } = data || {}
  const { attributesSummary } = equipment || { attributesSummary: '' }
  const { amount } = coveredBid || { amount: '' }
  const { driverPhone, shipperPodId, totalMiles } = info || {
    totalMiles: -1,
    driverPhone: '',
    shipperPodId: '',
  }
  const { carrierRate, strategicBuyRate, tcmCarrierPrices } = carrierPayment || {}
  const { cmTarget, listPrice } = tcmCarrierPrices || {
    cmTarget: { amount: undefined },
    listPrice: undefined,
  }
  const { includeListRate, rate } = loadBoardPost || { rate: '', includeListRate: true }
  const { targetMarginRate, total } = customerCharges || {}
  const { currentStopIndex, key } = status || {
    key: '',
    currentStopIndex: 0,
  }

  // Manual ETA calculations
  const currentStop = stops
    ? stops[currentStopIndex]
    : {
        appointment: { startAt: '', endAt: '', appointmentType: '', timezone: '' },
        manualEta: '',
        project44Eta: undefined,
      }
  const { appointment, manualEta: stopManualEta, project44Eta } = currentStop
  const { appointmentType, endAt, startAt, timezone } = appointment || {}

  let scheduledEta
  let lateDuration
  let formattedManualEta = {
    lateDuration: '',
    isLate: false,
    dateTime: '',
  } as IManualEta
  let formattedProject44Eta = {
    lateDuration: '',
    isLate: false,
    dateTime: '',
  } as IManualEta

  if (currentStop !== undefined && appointment !== undefined) {
    scheduledEta = startAt
    if (appointmentType === 'FCFS') {
      const d = moment.tz(endAt, timezone).subtract(2, 'hours')
      scheduledEta = d.format()
    }
    if (stopManualEta) {
      formattedManualEta = new ManualEta(stopManualEta, scheduledEta, timezone) as IManualEta
      lateDuration =
        stopManualEta && formattedManualEta.lateDuration && formattedManualEta.isLate
          ? `(${formattedManualEta.lateDuration} late)`
          : ''
    }
    if (project44Eta) {
      formattedProject44Eta = new ManualEta(project44Eta, scheduledEta, timezone) as IManualEta
      lateDuration =
        project44Eta && formattedProject44Eta.lateDuration && formattedProject44Eta.isLate
          ? `(${formattedProject44Eta.lateDuration} late)`
          : ''
    }
  }

  const dropTrailerStops = displayDropTrailerStops(stops)
  const hasDropTrailer = dropTrailerStops.length > 1

  const isCovered = key === PENDING_SHIPMENT_STATUSES[0]
  const marginRate = isCovered && amount ? amount : carrierRate
  const coveredMarginDelta = Number(total) - Number(marginRate)

  const equipmentDetails = attributesSummary.replace(/(,\s)/gim, ' \u00b7 ')

  // appointment scheduling info should only be seen on 'On Hold' shipments
  const stopsNew = ON_HOLD_STATUS.includes(key)
    ? stops.map(stop => ({
        ...stop,
        appointment: {
          ...stop.appointment,
          ...getApptUpdatedConfirmedInfo(stop.appointment, stop.facility.detail.schedulingOption),
        },
      }))
    : stops

  return {
    ...data,
    stops: stopsNew,
    equipment: {
      ...data.equipment,
      equipmentDetails,
    },
    status: {
      ...data.status,
      isCovered,
      isTendered: key === TENDERED_SHIPMENT_STATUS[0],
      isSubmittedToTendered: [
        ...TENDERED_SHIPMENT_STATUS,
        ...ON_HOLD_STATUS,
        ...SUBMITTED_STATUSES,
      ].includes(key),
    },
    info: {
      ...data.info,
      dropTrailerStops,
      hasDropTrailer,
      formattedDriverPhone: formatPhone(driverPhone),
      previousStopIndex: currentStopIndex > 0 ? currentStopIndex - 1 : 0,
      formattedManualEta: { dateTime: formattedManualEta.dateTime, lateDuration },
      formattedProject44Eta: {
        dateTime: formattedProject44Eta.dateTime,
        lateDuration,
        date: formattedProject44Eta.date,
        time: formattedProject44Eta.time,
      },
      isCeAssignmentGroup: [1, 2, 3].includes(parseInt(shipperPodId, 10)),
    },
    formattedCurrency: {
      coveredBidAmount: formatCurrency(amount),
      coveredBidPerMile: formatPerMile(amount, totalMiles),
      carrierRateAmount: formatCurrency(carrierRate),
      carrierRatePerMile: formatPerMile(carrierRate, totalMiles),
      strategicBuyRateAmount: formatCurrency(strategicBuyRate),
      strategicBuyRatePerMile: formatPerMile(strategicBuyRate, totalMiles),
      ceilingRateAmount: formatCurrency(cmTarget.amount),
      ceilingRatePerMile: formatPerMile(cmTarget.amount, totalMiles),
      listRateAmount: formatCurrency(listPrice),
      listRatePerMile: formatPerMile(listPrice, totalMiles),
      loadBoardRateAmount: `DAT ${rate && includeListRate ? formatCurrency(rate) : '(No Rate)'}`,
      customerTotalAmount: formatCurrency(total),
      customerTotalPerMile: formatPerMile(total, totalMiles),
      targetMarginRateAmount: formatCurrency(targetMarginRate),
      coveredMarginDeltaAmount: formatCurrency(coveredMarginDelta, undefined, undefined, true),
    },
  }
}
